'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { login } from '@/lib/api/auth';
import { useAuth } from '@/lib/context/AuthContext';
import { AuthShell, AuthDivider } from '@/components/auth/AuthShell';
import { Button, Field, Input, PasswordInput, Checkbox } from '@/components/ui';

const GoogleMark = (
  <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.9z" />
    <path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.7c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M6 14.4a6.6 6.6 0 0 1 0-4.2V7.4H2.3a11 11 0 0 0 0 9.8z" />
    <path fill="#EA4335" d="M12 5.4c1.6 0 3 .5 4.1 1.6l3.1-3.1A11 11 0 0 0 2.3 7.4L6 10.2c.9-2.6 3.2-4.4 6-4.4z" />
  </svg>
);
const AppleMark = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.5c0-2.4 2-3.6 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.1-2.9.9-3.6.9s-1.9-.9-3.1-.8c-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.3 1.2-.1 1.6-.8 3.1-.8s1.8.8 3.1.7c1.3 0 2.1-1.1 2.9-2.3.6-.8.8-1.3 1.3-2.2-3.4-1.3-2.7-5.7-1.6-6.5zM14.6 5.1c.7-.8 1.1-2 1-3.1-1 0-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1 0 2.2-.5 2.8-1.3z" />
  </svg>
);
const ErrorIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

export default function LoginPage() {
  const router = useRouter();
  const { setSession, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      router.replace('/home');
    }
  }, [user, authLoading, router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const u = await login({ email, password });
      if (!u.token) throw new Error('Token tidak ditemukan dari server');
      setSession(u.token, u);
      router.push('/home');
    } catch (err: any) {
      setError(err?.message || 'Sign in gagal');
    } finally {
      setLoading(false);
    }
  }

  // Tombol sosial: handshake OAuth (id_token) belum ada di UI. Fungsi googleLogin/appleLogin
  // sudah tersedia di lib/api/auth.ts untuk disambungkan saat SDK ditambahkan.
  const comingSoon = (label: string) => toast(`${label} akan segera tersedia`);

  return (
    <AuthShell
      headline="Rapat daring untuk instansi, tanpa gangguan."
      sub="Terenkripsi, terekam aman di cloud instansi, dirancang agar fokus tetap pada percakapan."
      stats={[
        { num: '12k+', label: 'Rapat / bulan' },
        { num: '99.9%', label: 'Uptime' },
        { num: 'AES-256', label: 'Enkripsi' },
      ]}
    >
      <h1 className="mx-h1" style={{ fontSize: 28, marginBottom: 'var(--mx-sp-2)' }}>Masuk</h1>
      <p className="mx-body" style={{ marginBottom: 26 }}>Gunakan akun instansi Anda untuk melanjutkan.</p>

      <div style={{ display: 'flex', gap: 'var(--mx-sp-3)' }}>
        <Button variant="secondary" block onClick={() => comingSoon('Login Google')}>{GoogleMark} Google</Button>
        <Button variant="secondary" block onClick={() => comingSoon('Login Apple')}>{AppleMark} Apple</Button>
      </div>

      <AuthDivider>atau email</AuthDivider>

      <form onSubmit={handleSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" placeholder="nama@instansi.go.id" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>

        <div style={{ height: 'var(--mx-sp-4)' }} />

        <Field label="Kata sandi" htmlFor="pw" error={error ? <>{ErrorIcon}{error}</> : undefined}>
          <PasswordInput id="pw" autoComplete="current-password" placeholder="Kata sandi" value={password} onChange={(e) => setPassword(e.target.value)} invalid={!!error} required />
        </Field>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 22px' }}>
          <Checkbox label="Ingat saya" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <Link href="/forgot-password" className="mx-btn mx-btn--ghost mx-btn--sm" style={{ color: 'var(--mx-accent)' }}>
            Lupa sandi?
          </Link>
        </div>

        <Button type="submit" variant="primary" size="lg" block loading={loading}>Masuk</Button>
      </form>

      <p className="mx-caption" style={{ textAlign: 'center', marginTop: 22 }}>
        Belum punya akun?{' '}
        <Link href="/register" style={{ color: 'var(--mx-accent)', fontWeight: 600, textDecoration: 'none' }}>Daftar</Link>
      </p>
    </AuthShell>
  );
}
